import React, { useCallback, useEffect, useState } from "react";
import { API_BASE_URL } from "./config";
import "./GlobalJobsApplications.css";

const PAGE_SIZE = 25;

function displayDate(value) {
  if (!value) return "Pending";
  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? "Unavailable"
    : date.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
}

// GTJ MY APPLICATIONS PREMIUM V1
export default function GlobalJobsMyApplications({ token }) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    if (!token) return;

    setBusy(true);
    setError("");

    try {
      const response = await fetch(
        `${API_BASE_URL}/global-tech-jobs/applications/me?page=${page}&page_size=${PAGE_SIZE}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          typeof data.detail === "string"
            ? data.detail
            : `Unable to load applications (${response.status})`
        );
      }

      setItems(Array.isArray(data.items) ? data.items : []);
      setTotalPages(
        Math.max(1, Number(data.total_pages) || 1)
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, [token, page]);

  useEffect(() => {
    refresh();

    const listener = () => {
      if (page === 1) refresh();
      else setPage(1);
    };

    window.addEventListener(
      "gtj-applications-changed",
      listener
    );

    return () => {
      window.removeEventListener(
        "gtj-applications-changed",
        listener
      );
    };
  }, [refresh, page]);

  return (
    <section className="gtjapp-history">
      <header className="gtjapp-history-heading">
        <div>
          <small>CANDIDATE DASHBOARD</small>
          <h3>My Applications</h3>
          <p>Track submitted jobs and screening progress.</p>
        </div>

        <button
          type="button"
          onClick={refresh}
          disabled={busy}
        >
          {busy ? "Refreshing..." : "Refresh"}
        </button>
      </header>

      {error && (
        <p className="gtjapp-error" role="alert">
          {error}
        </p>
      )}

      {!busy && !error && items.length === 0 && (
        <p>No applications submitted yet.</p>
      )}

      {items.length > 0 && (
        <div className="gtjapp-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Application ID</th>
                <th>Job</th>
                <th>Submitted</th>
                <th>Status</th>
                <th>AI Screening</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.application_id}>
                  <td>{item.application_id}</td>
                  <td>
                    <strong>{item.job_title}</strong>
                    <small>{item.company}</small>
                  </td>
                  <td>{displayDate(item.submitted_at)}</td>
                  <td>
                    <span className={
                      "gtjapp-status gtjapp-status-" +
                      String(item.status || "submitted")
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")
                    }>
                      <span className="gtjapp-status-dot" />
                      {String(item.status || "submitted")
                        .replace(/_/g, " ")}
                    </span>
                  </td>
                  <td>
                    {String(
                      item.ai_screening_status || "pending"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="gtjapp-pagination">
          <button
            type="button"
            disabled={busy || page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button
            type="button"
            disabled={busy || page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </section>
  );
}
